import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Copy, Link, Check } from 'lucide-react';
import { quickCopyService } from '../services';
import { messageHandler } from '../services/message-handler.service';
import type { QuickCopyInput } from '../types/quickCopy';
import PageHeader from '../components/ui/PageHeader';
import {
  Form,
  FormField,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  Button,
} from '../components/ui';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';

function QuickCopy() {
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Mutation for creating a quick copy entry
  const createMutation = useMutation({
    mutationFn: (data: QuickCopyInput) => quickCopyService.createQuickCopy(data),
    onSuccess: response => {
      const publicKey = response.public_key.replace(/-/g, '');
      const publicLink = `${window.location.origin}/quick-copy/${publicKey}`;
      setGeneratedLink(publicLink);
      messageHandler.success('Link gerado com sucesso!');
    },
    onError: error => {
      messageHandler.error(error);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data: QuickCopyInput = {
      type: formData.get('type') as 'pix' | 'barcode' | 'text' | 'url',
      value: formData.get('value') as string,
      label: formData.get('label') as string,
    };

    createMutation.mutate(data);
  };

  const copyToClipboard = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="py-6">
      <div className="px-4 sm:px-6 lg:px-8">
        <PageHeader
          title="Copia Fácil"
          description="Crie links para compartilhar conteúdo que pode ser copiado rapidamente"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle>Criar Novo Link</CardTitle>
            </CardHeader>
            <CardContent>
              <Form onSubmit={handleSubmit}>
                <FormField>
                  <FormLabel htmlFor="type" required>
                    Tipo de Conteúdo
                  </FormLabel>
                  <FormSelect id="type" name="type" required>
                    <option value="pix">PIX</option>
                    <option value="barcode">Código de Barras</option>
                    <option value="text">Texto</option>
                    <option value="url">URL</option>
                  </FormSelect>
                </FormField>

                <FormField>
                  <FormLabel htmlFor="label">Descrição (opcional)</FormLabel>
                  <FormInput
                    id="label"
                    name="label"
                    placeholder="Uma descrição para identificar este conteúdo"
                  />
                </FormField>

                <FormField>
                  <FormLabel htmlFor="value" required>
                    Conteúdo
                  </FormLabel>
                  <FormTextarea
                    id="value"
                    name="value"
                    rows={5}
                    placeholder="Digite o conteúdo que será copiado"
                    required
                  />
                </FormField>

                <Button type="submit" className="mt-4 w-full" isLoading={createMutation.isPending}>
                  Gerar Link
                </Button>
              </Form>
            </CardContent>
          </Card>

          {generatedLink && (
            <Card>
              <CardHeader>
                <CardTitle>Link Gerado</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">
                    Compartilhe este link para permitir que outras pessoas copiem o conteúdo
                    rapidamente:
                  </p>

                  <div className="flex items-center space-x-2">
                    <div className="flex-1 p-3 bg-gray-50 dark:bg-gray-800 rounded-md overflow-x-auto">
                      <code className="text-sm break-all">{generatedLink}</code>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={copyToClipboard}
                      className="flex-shrink-0"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  <div className="flex justify-center">
                    <Button
                      variant="secondary"
                      onClick={() => window.open(generatedLink, '_blank')}
                      className="flex items-center space-x-2"
                    >
                      <Link className="h-4 w-4 mr-2" />
                      Abrir Link
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuickCopy;
